import React from 'react';
import styled from 'styled-components';
import bgSoftware from '../../images/bg-software.png';
import PulsingLights from '../UI/PulsingLights';

const StyledHeroSection = styled.section`
  min-height: 100vh;
  background: 
    linear-gradient(135deg, rgba(10, 14, 39, 0.0) 0%, rgba(26, 31, 58, 0.1) 50%, rgba(15, 20, 40, 0.3) 100%),
    url(${bgSoftware});
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 40%, rgba(81, 194, 201, 0.06) 0%, transparent 35%),
      radial-gradient(circle at 70% 60%, rgba(81, 194, 201, 0.04) 0%, transparent 35%);
    animation: bloom 10s ease-in-out infinite;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(ellipse at 20% 30%, rgba(81, 194, 201, 0.05) 0%, transparent 40%),
      radial-gradient(ellipse at 80% 70%, rgba(81, 194, 201, 0.03) 0%, transparent 40%);
    animation: bloom 8s ease-in-out infinite reverse;
    pointer-events: none;
  }

  @keyframes bloom {
    0%, 100% { 
      opacity: 0.4;
      transform: scale(1);
    }
    50% { 
      opacity: 0.7;
      transform: scale(1.02);
    }
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 120px 20px 80px;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.5rem, 8vw, 5rem);
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
  
  background: linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1.1rem, 3vw, 1.5rem);
  color: #94a3b8;
  margin-bottom: 3rem;
  line-height: 1.6;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #51c2c9 0%, #2eb8bf 100%);
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(81, 194, 201, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 30px rgba(81, 194, 201, 0.5);
    color: #ffffff;
    text-decoration: none;
  }
`;

interface HeroSectionProps {
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
  children?: React.ReactNode;
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
  title, 
  subtitle, 
  ctaText, 
  ctaLink,
  children 
}) => {
  return (
    <StyledHeroSection id="home">
      <PulsingLights />
      <HeroContent>
        <HeroTitle dangerouslySetInnerHTML={{ __html: title }} />
        <HeroSubtitle>{subtitle}</HeroSubtitle>
        <CTAButton href={ctaLink}>{ctaText}</CTAButton>
      </HeroContent>
      {children}
    </StyledHeroSection>
  );
};

export default HeroSection;

